import { Body, Heading, Icon, Spinner } from '@assembly-js/design-system';
import { withStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { Channel as ChannelType, StreamChat } from 'stream-chat';
import * as ChatCSS from 'stream-chat-react/dist/css/v2/index.css';

import { AssistantEmptyState } from 'src/features/assistant/AssistantEmptyState';
import { InternalChatContent } from 'src/features/assistant/InternalChatContent';
import { InternalChatProvider } from 'src/features/assistant/internalChatContext';
import { ChannelProvider } from 'src/features/channel/ChannelProvider';
import { InnerChannelContent } from 'src/features/channel/InnerChannelContent';
import { NoMessagesEmptyState } from 'src/features/channel/NoMessagesEmptyState';
import { CustomMessageNotification } from 'src/features/messages/CustomMessageNotification';
import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';
import { useIsProxySession } from 'src/hooks/useIsProxySession';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
// TODO in POR-15598: move this out of legacy (or even better, figure out a way to initialize the client universally so this isn't needed)
// eslint-disable-next-line no-restricted-imports
import { useChatClient } from 'src/legacy/components/Chat/useChatClient';
import { useMessagesStyles } from 'src/routes/messaging/MessagesPageStyles';
import { useGetPortalAssistantSettingsQuery } from 'src/services/api';
import {
  AIAssistantStatus,
  clearStatus,
  setStatus,
} from 'src/store/aiAssistantStatus/slice';

type EntityType = 'client' | 'company';

interface InternalChatProps {
  entityType: EntityType;
  /** Either a clientId or a companyId */
  entityId: string;
  internalChat?: ChannelType;
  messageId?: string;
}

const getInternalChannel = async (
  chatClient: StreamChat,
  entityType: EntityType,
  entityId: string,
  currentUserGetStreamId: string,
) => {
  const internalChannels = await chatClient.queryChannels({
    type: 'internal',
    members: {
      $in: [currentUserGetStreamId],
    },
    [entityType === 'client' ? 'clientID' : 'companyID']: entityId,
  });
  if (internalChannels.length > 1) {
    throw new Error(
      `Incorrect number of channels returned, expected 1, got ${internalChannels.length}`,
    );
  } else {
    return internalChannels.at(0);
  }
};

const InternalChatComponent = ({
  entityType,
  entityId,
  internalChat,
  messageId,
}: InternalChatProps) => {
  const chatClient = useChatClient();
  const dispatch = useAppDispatch();
  const { data: portalAssistantSettings } =
    useGetPortalAssistantSettingsQuery();

  const currentUser = useAppSelector((state) => state.user);
  const currentUserGetStreamId = currentUser.data?.getstreamId || '';

  const isProxySession = useIsProxySession(currentUser.id);

  const [internalChannel, setInternalChannel] = useState<
    ChannelType | undefined
  >(undefined);
  const [isError, setIsError] = useState(false);

  // Get AI assistant status for this channel
  const aiAssistantStatus = useAppSelector((state) =>
    internalChannel?.cid
      ? state.aiAssistantStatus.statuses[internalChannel.cid]
      : null,
  );

  // Callback functions for AI assistant status management
  const handleSetAIAssistantStatus = useCallback(
    (
      channelId: string,
      status: string,
      metadata?: { tool?: string; query?: string },
      statusMessageId?: string,
    ) => {
      dispatch(
        setStatus({
          channelId,
          status: status as AIAssistantStatus,
          metadata,
          messageId: statusMessageId,
        }),
      );
    },
    [dispatch],
  );

  const handleClearAIAssistantStatus = useCallback(() => {
    if (internalChannel?.cid) {
      dispatch(clearStatus({ channelId: internalChannel.cid }));
    }
  }, [dispatch, internalChannel?.cid]);

  useEffect(() => {
    if (internalChat) {
      // if a channel is provided, we don't need to fetch it
      setInternalChannel(internalChat);
      return;
    }
    const fetchInternalChannel = async () => {
      if (chatClient) {
        for (let attempt = 1; attempt <= 3; attempt++) {
          const fetchedInternalChannel = await getInternalChannel(
            chatClient,
            entityType,
            entityId,
            currentUserGetStreamId,
          );
          if (fetchedInternalChannel) {
            setInternalChannel(fetchedInternalChannel);
            return;
          }
          // no internal channel was fetched, retry in 500ms
          if (attempt < 3) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          } else {
            throw new Error('No channels found');
          }
        }
      }
    };
    fetchInternalChannel().catch((e) => {
      console.error(e);
      setIsError(true);
    });
  }, [chatClient, currentUserGetStreamId, entityType, entityId, internalChat]);

  const { isSidebarTheme } = useMessagingTheme();
  // TODO: potentially either refactor for tailwind or move out of massaging routes
  const classes = useMessagesStyles({
    showMessagingSidebar: false,
    isSidebarTheme,
  });

  const AssistantEmpty = useCallback(
    () => <AssistantEmptyState entityType={entityType} />,
    [entityType],
  );

  if (isError) {
    return (
      <div className="w-full h-full relative">
        <div className="w-2/3 absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-center">
          <Icon
            icon="Warning"
            className="w-7 h-7 text-warning-primary mb-[18px]"
          />
          <Heading size="xl" className="text-black-heading mb-2">
            Can&apos;t load chat
          </Heading>
          <Body size="lg" className="text-gray-small">
            Something went wrong. Please try refreshing the page.
          </Body>
        </div>
      </div>
    );
  }

  return (
    <>
      {internalChannel ? (
        <div className={classes.root}>
          <InternalChatProvider isInternalChat>
            <ChannelProvider
              MessageNotification={CustomMessageNotification}
              EmptyStateIndicator={
                portalAssistantSettings?.disableAssistant
                  ? NoMessagesEmptyState
                  : AssistantEmpty
              }
              channel={internalChannel}
              markReadOnMount={!isProxySession}
              aiAssistantStatus={aiAssistantStatus}
            >
              <InnerChannelContent channel={internalChannel}>
                <InternalChatContent
                  placeholderText={
                    portalAssistantSettings?.disableAssistant
                      ? 'Chat with teammates'
                      : 'Chat with teammates or @Assembly'
                  }
                  messageId={messageId}
                  isInternalChat={true}
                  setAIAssistantStatus={handleSetAIAssistantStatus}
                  clearAIAssistantStatus={handleClearAIAssistantStatus}
                />
              </InnerChannelContent>
            </ChannelProvider>
          </InternalChatProvider>
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size={10} />
        </div>
      )}
    </>
  );
};

// TODO: figure out how to apply these styles without material UI
export const InternalChat = React.memo(
  withStyles(ChatCSS)(InternalChatComponent),
);
